import { queryKeys } from '@sportninja/common/config/queryKeys'
import {
  listMembershipEventsPage,
  type ListMembershipEventsQuery,
} from '@sportninja/common/services/event'
import { EVENTS_PER_PAGE_STREAM } from '../constants'
import type { ScheduleOrder } from '../filterEvents'
import { useEventStream, type EventStream } from './useEventStream'

/**
 * The GAMES query, a superset of the events one: it also carries
 * `membership_type` and `page`, neither of which `/events/membership` takes.
 * Typed as the superset because that is what both schedule screens hand over —
 * this hook narrows it before sending.
 */
export type MembershipScheduleQuery = ListMembershipEventsQuery & {
  membership_type?: string
  page?: number
}

export interface UseMembershipEventStreamArgs {
  /**
   * The SAME filter object the games request sends, date window included.
   * Games-only keys are stripped inside; everything else passes through, so the
   * two lists can never be bounded differently — that divergence is exactly
   * what this hook exists to prevent.
   */
  query: MembershipScheduleQuery
  /** False while the filters are still resolving; keeps the query idle. */
  enabled?: boolean
}

/**
 * The viewer's own events, paginated — `/events/membership`.
 *
 * The event-side counterpart of `/games/follow`: one request answers for every
 * team the user follows, taking the same filters the games call takes.
 *
 * Shared by web's My Schedule and mobile's Schedule tab. It lives in `common/`
 * rather than beside either screen because both send the same query to the same
 * endpoint; two copies would be free to drift on page size or on which
 * games-only keys get stripped.
 */
export const useMembershipEventStream = ({
  query,
  enabled = true,
}: UseMembershipEventStreamArgs): EventStream => {
  'use memo'

  // `membership_type` and `page` are games-only. Stripped HERE rather than at
  // the call sites so each screen keeps handing over ONE shared object — the
  // moment a caller builds its own query, the two lists can drift apart again.
  // Also stripped from the key, so toggling a filter this endpoint ignores
  // cannot invalidate its cache for nothing.
  //
  // Deleted from a copy rather than destructured away: naming the discarded
  // keys only in order to ignore them trips `no-unused-vars` under the config
  // covering `common/`, however they are spelled.
  const eventsQuery: MembershipScheduleQuery = {
    ...query,
    per_page: EVENTS_PER_PAGE_STREAM,
  }
  delete eventsQuery.membership_type
  delete eventsQuery.page
  const order = (eventsQuery.order as ScheduleOrder) ?? 'asc'

  return useEventStream({
    queryKey: queryKeys.events.byMembershipStream(eventsQuery),
    fetchPage: (page) => listMembershipEventsPage(eventsQuery, page),
    enabled,
    order,
  })
}
